/** @typedef {import('../index.js').InvalidDocData} InvalidDocData */
/** @typedef {import('./part.js').PartDocData} PartDocData */
import * as Yup from 'yup';
import {
  PartTypes,
  StepTypes,
} from '../../../constants/index.js';

import FluidicLayer, { fluidicLayerSchema } from './fluidicLayer.js';
import Part, { partSchema } from './part.js';
import Piston, { pistonSchema } from './piston.js';
import Tool, { toolSchema } from './tool.js';

/**
 * Part types that are physical instances of a named design: their pPartId
 * is `partId-revision-name-instance` (instead of `partId-revision-material`)
 * so ids stay human-readable in pickers and process logs, and the part
 * editor numbers their instances automatically. Single source of truth for
 * getPPartId / getPPartLabel and the editor's instance handling.
 */
const InstancedPartTypes = Object.freeze([PartTypes.TOOL, PartTypes.PISTON]);

/**
 * Suggested metadata keys per part type, in the `{ label, options }` group
 * shape the part editor's Autosuggest takes, so it can be indexed by type
 * and passed straight through. Free text is still allowed; these just keep
 * common keys from drifting into near-duplicate spellings.
 */
const PPartMetadataKeyOptions = Object.freeze({
  [PartTypes.PRODUCT]: [
    {
      label: 'Molding',
      options: [
        { value: 'Thickness' },
        { value: 'Thickness Tolerance' },
      ],
    },
  ],
  [PartTypes.PISTON]: [
    {
      label: 'Inspection',
      options: [
        { value: 'Back alignment slot inspection at DOM' },
        { value: 'Side alignment slot inspection at DOM' },
      ],
    },
  ],
});

/**
 * Converts a Firestore document snapshot into a {@link Part} or subclass instance.
 * @param {any} doc Raw Firestore document snapshot.
 * @returns A {@link Part} or subclass instance.
 */
function pPartFromFirestore(doc) {
  const docData = { id: doc.id, ...doc.data() };
  try {
    switch (docData.type) {
      case PartTypes.TOOL:
        return Tool.fromFirestore(docData);
      case PartTypes.PISTON:
        return Piston.fromFirestore(docData);
      case PartTypes.PRODUCT:
        return FluidicLayer.fromFirestore(docData);
      default:
        return Part.fromFirestore(docData);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error transforming project part data:', docData.id, error);
    /** @type {InvalidDocData} */
    const InvalidDocData = { validation: 'failed', ...docData };
    return InvalidDocData;
  }
}

/**
 * Converts a {@link Part} instance into a Firestore-compatible object.
 * @param {Part} part A {@link Part} instance.
 * @returns A {@link PartDocData} object (without `id`).
 */
function pPartToFirestore(part, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  switch (part.type) {
    case PartTypes.TOOL: {
      const tool = new Tool(part);
      return tool.toFirestore({ serverTimestamp });
    }
    case PartTypes.PISTON: {
      const piston = new Piston(part);
      return piston.toFirestore({ serverTimestamp });
    }
    case PartTypes.PRODUCT: {
      const fluidicLayer = new FluidicLayer(part);
      return fluidicLayer.toFirestore({ serverTimestamp });
    }
    default: {
      const newPart = new Part(part);
      return newPart.toFirestore({ serverTimestamp });
    }
  }
}

/**
 * Returns the appropriate Yup schema for a given tool type.
 * @param {string} type - Type of tool.
 * @returns A Yup schema.
 */
function getPPartSchemaByType(type) {
  switch (type) {
    case PartTypes.TOOL:
      return toolSchema;
    case PartTypes.PISTON:
      return pistonSchema;
    case PartTypes.PRODUCT:
      // Not all existing FluidicLayer parts have stepType, so we need to ensure it's optional,
      // but required for new parts. This is a workaround to ensure backward compatibility.
      return fluidicLayerSchema.shape({
        stepType: Yup.string().required('Step Type is required'),
        notes: Yup.string().when('stepType', {
          is: StepTypes.CONNECTORIZING,
          then: Yup.string().required('Include number and type of connectors in the notes'),
          otherwise: Yup.string(),
        }),
      });
    default:
      return partSchema;
  }
}

/**
 * Get the PPart (project part) ID from a {@link Part} instance.
 * Instanced types (tools, pistons): `partId-revision-name-instance`;
 * everything else: `partId-revision-material`.
 * @param {Part} transformedPart - Instance of {@link Part}.
 * @returns {string} PPart (project part) ID.
 */
function getPPartId(transformedPart) {
  const idArr = [transformedPart.partId, transformedPart.revision];
  if (InstancedPartTypes.includes(transformedPart.type)) {
    idArr.push(transformedPart.name, transformedPart.instance);
  } else {
    idArr.push(transformedPart.material);
  }
  const pPartId = idArr.filter(Boolean).join('-');
  return pPartId;
}

/**
 * @typedef {Object} DecodedPPartId
 * @property {string} partId
 * @property {string} revision
 * @property {string} [name]
 * @property {string} [instance]
 * @property {string} [material]
 */
/**
 * Decodes the individual components of a PPart (project part) ID.
 * @param {string} pPartId - PPart (project part) ID.
 * @param {boolean} [isTool = false] - Is the part an instanced type (tool or
 * piston), whose id carries name and instance instead of material?
 * @returns {DecodedPPartId} A {@link DecodedPPartId} object.
 * @throws {Error} `pPartId` is not a string or has an invalid format.
 */
function decodePPartId(pPartId, isTool = false) {
  if (typeof pPartId !== 'string') {
    throw new Error('Invalid parameter: pPartId must be a string.');
  }
  const [partId, revision, thirdField, forthField] = pPartId.split('-');
  if (!partId || !revision) {
    throw new Error('Invalid pPartId format');
  }

  const result = {
    partId,
    revision,
  };

  if (isTool) {
    result.name = thirdField;
    result.instance = forthField;
  } else {
    result.material = thirdField;
  }

  return result;
}

/**
 * The full project part ID display string, e.g. "P068-0068-A-COC" —
 * the project name prefixed onto {@link getPPartId}. Single source of
 * truth for the format; display components append name/type themselves.
 * @param {Part} part - Instance of {@link Part}.
 * @param {string} [projectName] - Project name prefix; omitted when absent.
 * @returns {string} Full PPart ID, or '' without a part.
 */
function getFullPPartId(part, projectName) {
  if (!part) return '';
  return [projectName, getPPartId(part)].filter(Boolean).join('-');
}

/**
 * The standard one-line part label, e.g.
 * "P068-0068-A-COC (Pipette interface - Product)". Single source of
 * truth for the format used by part badges and displays. Tool and piston
 * IDs already carry the name (getPPartId), so their parenthetical is just
 * the type — no "…-Alignment jig-1 (Alignment jig - Tool)" repeats.
 * @param {Part} part - Instance of {@link Part}.
 * @param {string} [projectName] - Project name prefix; omitted when absent.
 * @returns {string} Display label, or '' without a part.
 */
function getPPartLabel(part, projectName) {
  if (!part) return '';
  const detail = InstancedPartTypes.includes(part.type) ? part.type : `${part.name} - ${part.type}`;
  return `${getFullPPartId(part, projectName)} (${detail})`;
}

/**
 * Comparator for project part lists, newest-looking first: partId DESC,
 * then revision DESC, then instance DESC, then createdAt DESC. Single
 * source of truth for pPart ordering (pickers, providers).
 * @param {Part} a - Instance of {@link Part}.
 * @param {Part} b - Instance of {@link Part}.
 * @returns {number} Standard comparator result.
 */
function comparePPartsDesc(a, b) {
  // 1) partId: '0003', '0002', '0001', DESC
  if (a.partId !== b.partId) {
    return b.partId - a.partId;
  }

  // 2) revision: 'C', 'B', 'A', DESC
  if (a.revision !== b.revision) {
    return b.revision.localeCompare(a.revision);
  }

  // 3) instance: optional number, DESC
  const instA = (typeof a.instance === 'number') ? a.instance : -Infinity;
  const instB = (typeof b.instance === 'number') ? b.instance : -Infinity;
  if (instA !== instB) return instB - instA;

  // 4) createdAt: latest first
  const timeA = a.createdAt?.toMillis?.() ?? 0;
  const timeB = b.createdAt?.toMillis?.() ?? 0;
  return timeB - timeA;
}

export {
  InstancedPartTypes,
  PPartMetadataKeyOptions,
  comparePPartsDesc,
  pPartFromFirestore,
  pPartToFirestore,
  getPPartSchemaByType,
  getFullPPartId,
  getPPartId,
  getPPartLabel,
  decodePPartId,
};
